import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import { colors } from "../../dark-hero/colors";
import { PageContainer } from "../../dark-hero/PageContainer";
import { DISCORD_INVITE_URL } from "../../../public-site/siteData";
import { SUPPORT_EMAIL } from "../publicFacts";
import { GOVERNING_COUNTRY } from "../legalController";
import {
  DARK_HERO_HEADER_HEIGHT_PX,
  DARK_HERO_HEADER_XL_HEIGHT_PX,
} from "../../publicHeaderLayout";

const PAGE_BG = "#080808";
const LAST_UPDATED = "16 October 2026";
const POLICY_VERSION = "1.0";

const TOC_ITEMS = [
  { id: "s1", num: "1.", label: "Information we collect" },
  { id: "s2", num: "2.", label: "How we use it" },
  { id: "s3", num: "3.", label: "How we share it" },
  { id: "s4", num: "4.", label: "Cookies" },
  { id: "s5", num: "5.", label: "Data security" },
  { id: "s6", num: "6.", label: "Your rights" },
  { id: "s7", num: "7.", label: "Children's privacy" },
  { id: "s8", num: "8.", label: "Policy changes" },
  { id: "s9", num: "9.", label: "Contact us" },
];

const RIGHTS = [
  { name: "Access", desc: "Request a copy of the personal information we hold about you at any time." },
  {
    name: "Correction",
    desc: "Ask us to correct or update inaccurate or incomplete information on your account.",
  },
  {
    name: "Deletion",
    desc: "Request deletion of your account and associated data, subject to certain legal retention obligations.",
  },
  {
    name: "Opt-out",
    desc: "Opt out of marketing communications by following the unsubscribe link in any such message.",
  },
];

const sectionLabelSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: colors.text.muted,
  mb: 1,
};

const sectionTitleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "22px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.text.primary,
  mb: 2.75,
  lineHeight: 1.1,
};

const bodySx = {
  fontSize: "15px",
  color: colors.text.secondary,
  lineHeight: 1.85,
  mb: 2,
  "& strong": { color: colors.text.primary, fontWeight: 600 },
  "& a": { color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
};

function DocList({ items }) {
  return (
    <Box sx={{ my: 2.5 }}>
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "150px 1fr" },
            gap: { xs: 0.5, sm: 2.5 },
            py: 2,
            borderBottom: `1px solid ${colors.border.subtle}`,
            "&:first-of-type": { borderTop: `1px solid ${colors.border.subtle}` },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: colors.text.primary,
              pt: { sm: "1px" },
            }}
          >
            {item.key}
          </Typography>
          <Typography sx={{ ...bodySx, mb: 0 }}>{item.val}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function Callout({ children }) {
  return (
    <Box
      sx={{
        my: 3,
        p: "18px 22px",
        background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
        border: "1px solid rgba(232,163,61,0.25)",
        borderLeft: `2px solid ${colors.primary.main}`,
        "& p": {
          fontSize: "13px",
          color: colors.text.secondary,
          lineHeight: 1.7,
          m: 0,
        },
        "& strong": { color: colors.primary.main, fontWeight: 600 },
        "& a": { color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
      }}
    >
      {children}
    </Box>
  );
}

function DocSection({ id, num, title, children }) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        pt: "52px",
        borderTop: `1px solid ${colors.border.subtle}`,
        scrollMarginTop: { xs: `${DARK_HERO_HEADER_HEIGHT_PX + 16}px`, xl: `${DARK_HERO_HEADER_XL_HEIGHT_PX + 16}px` },
      }}
    >
      <Typography sx={sectionLabelSx}>Section {num}</Typography>
      <Typography component="h2" sx={sectionTitleSx}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function DiscordIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

const contactIconSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: colors.background.surface,
  border: `1px solid ${colors.border.subtle}`,
  color: colors.primary.main,
};

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = React.useState("s1");

  React.useEffect(() => {
    const sections = TOC_ITEMS.map((item) => document.getElementById(item.id)).filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-15% 0px -75% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleTocClick = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  return (
    <Box sx={{ backgroundColor: PAGE_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <PageContainer sx={{ maxWidth: "1140px", px: { xs: 0, md: 0 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            minHeight: "100vh",
          }}
        >
          <Box
            component="aside"
            sx={{
              width: { xs: "100%", md: "240px" },
              flexShrink: 0,
              position: { md: "sticky" },
              top: { md: `${DARK_HERO_HEADER_HEIGHT_PX}px`, xl: `${DARK_HERO_HEADER_XL_HEIGHT_PX}px` },
              alignSelf: "flex-start",
              maxHeight: {
                md: `calc(100vh - ${DARK_HERO_HEADER_HEIGHT_PX}px)`,
                xl: `calc(100vh - ${DARK_HERO_HEADER_XL_HEIGHT_PX}px)`,
              },
              overflowY: { md: "auto" },
              py: { xs: 3, md: 7 },
              px: { xs: 3, md: "40px 40px 56px 48px" },
              borderRight: { md: `1px solid ${colors.border.subtle}` },
              borderBottom: { xs: `1px solid ${colors.border.subtle}`, md: "none" },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: colors.text.muted,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&::before": {
                  content: '""',
                  width: 12,
                  height: "1px",
                  bgcolor: colors.text.muted,
                  display: "block",
                },
              }}
            >
              Contents
            </Typography>

            <Box component="nav" sx={{ display: "flex", flexDirection: "column" }}>
              {TOC_ITEMS.map((item) => {
                const active = activeSection === item.id;
                return (
                  <Box
                    key={item.id}
                    component="a"
                    href={`#${item.id}`}
                    onClick={(event) => handleTocClick(event, item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.25,
                      py: 1.125,
                      borderBottom: "1px solid rgba(255,255,255,0.03)",
                      textDecoration: "none",
                      fontSize: "12px",
                      lineHeight: 1.4,
                      color: active ? colors.primary.main : colors.text.muted,
                      transition: "color 0.15s",
                      "&:hover": { color: active ? colors.primary.main : colors.text.secondary },
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        flexShrink: 0,
                        width: 18,
                        color: active ? colors.primary.main : colors.text.muted,
                      }}
                    >
                      {item.num}
                    </Box>
                    {item.label}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ height: "1px", bgcolor: colors.border.subtle, my: 2.5 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {[
                { label: "Last updated", val: LAST_UPDATED },
                { label: "Version", val: POLICY_VERSION },
                { label: "Jurisdiction", val: `${GOVERNING_COUNTRY} / GDPR` },
              ].map((row) => (
                <Box key={row.label}>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: colors.text.muted,
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: colors.text.secondary }}>{row.val}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box
                component={Link}
                to="/data-protection-policy-and-gdpr"
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: "10px 14px",
                  textDecoration: "none",
                  border: `1px solid ${colors.border.subtle}`,
                  color: colors.text.muted,
                  transition: "color 0.2s, border-color 0.2s",
                  "&:hover": { color: colors.text.secondary, borderColor: colors.border.default },
                }}
              >
                GDPR Policy →
              </Box>
              <Box
                component={Link}
                to="/terms-of-use"
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: "10px 14px",
                  textDecoration: "none",
                  border: `1px solid ${colors.border.subtle}`,
                  color: colors.text.muted,
                  transition: "color 0.2s, border-color 0.2s",
                  "&:hover": { color: colors.text.secondary, borderColor: colors.border.default },
                }}
              >
                Terms of Service →
              </Box>
            </Box>
          </Box>

          <Box
            component="article"
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: "720px",
              py: { xs: 5, md: 7 },
              px: { xs: 3, md: "64px 48px 120px" },
              pb: { xs: 10, md: 15 },
            }}
          >
            <Box
              component="header"
              sx={{ mb: 7, pb: 5, borderBottom: `1px solid ${colors.border.subtle}` }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                  "&::before": {
                    content: '""',
                    width: 20,
                    height: "1px",
                    bgcolor: colors.primary.main,
                    display: "block",
                  },
                }}
              >
                Legal document
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "4.25rem" },
                  fontWeight: 700,
                  textTransform: "uppercase",
                  lineHeight: 0.92,
                  letterSpacing: "-0.01em",
                  color: colors.text.primary,
                  mb: 3,
                }}
              >
                Privacy
                <br />
                Policy
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 300,
                  color: colors.text.secondary,
                  lineHeight: 1.8,
                  mb: 3.5,
                }}
              >
                At Ouro, your privacy is taken seriously. This policy explains what data we collect, why we
                collect it, how it&apos;s used, and what rights you have over it. Plain language where possible.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                  pt: 2.5,
                  borderTop: `1px solid ${colors.border.subtle}`,
                }}
              >
                {[
                  { label: "Last updated", val: LAST_UPDATED },
                  { label: "Applies to", val: "ouro.is and all Ouro services" },
                  {
                    label: "Questions",
                    val: (
                      <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
                        {SUPPORT_EMAIL}
                      </Box>
                    ),
                  },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "9px",
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                        mb: 0.375,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: colors.text.secondary }}>{item.val}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <DocSection id="s1" num="01" title="Information we collect">
              <Typography sx={bodySx}>
                We collect the following types of information to provide and improve our services. We try to
                collect only what&apos;s needed to make the platform work properly.
              </Typography>
              <DocList
                items={[
                  {
                    key: "Personal info",
                    val: "When you register, you sign in with Steam. We receive your public Steam profile. That is all we need to create the account. An ID check is optional, runs through didit.me if you choose it, and is not part of signup.",
                  },
                  {
                    key: "Gameplay data",
                    val: "Match history, in-game statistics, and behavioral patterns collected during matches. Used to run anti-cheat detection, improve matchmaking accuracy, and build your reputation profile.",
                  },
                  {
                    key: "Technical data",
                    val: "IP address, device type, browser type, and operating system. Collected to ensure security and proper functionality of the service.",
                  },
                  {
                    key: "Location inference",
                    val: "When you haven't selected a country, we may use your IP address to infer your country for game server region suggestions. The derived country is stored as a non-authoritative fallback — your user-selected country always takes priority.",
                  },
                  {
                    key: "Communication data",
                    val: "Information from communications between you and Ouro — including support inquiries, feedback, and other correspondence.",
                  },
                ]}
              />
            </DocSection>

            <DocSection id="s2" num="02" title="How we use your information">
              <Typography sx={bodySx}>
                Ouro uses collected information for the following purposes. We don&apos;t use your data for
                advertising, and we don&apos;t sell it.
              </Typography>
              <DocList
                items={[
                  {
                    key: "Service provision",
                    val: "To operate and maintain the matchmaking service — managing your account, facilitating matches, and providing customer support.",
                  },
                  {
                    key: "Improvement",
                    val: "To analyze gameplay data and feedback, refine matchmaking and anti-cheat algorithms, and improve the overall experience.",
                  },
                  {
                    key: "Security",
                    val: "To detect and prevent cheating, fraudulent activity, and other forms of abuse on the platform.",
                  },
                  {
                    key: "Communication",
                    val: "To send important account updates, service notifications, and platform-related information.",
                  },
                  {
                    key: "Legal compliance",
                    val: "To comply with legal obligations and respond to lawful requests from authorities where required.",
                  },
                ]}
              />
            </DocSection>

            <DocSection id="s3" num="03" title="How we share your information">
              <Typography sx={bodySx}>
                We do not sell, trade, or rent your personal information to third parties. Full stop.
              </Typography>
              <Typography sx={bodySx}>
                Your information may be shared only in the following limited circumstances:
              </Typography>
              <DocList
                items={[
                  {
                    key: "Service providers",
                    val: "Trusted third-party companies that help us operate — hosting, infrastructure, and support platforms. These providers are contractually obligated to maintain confidentiality and cannot use your data for their own purposes.",
                  },
                  {
                    key: "Legal authorities",
                    val: "We may disclose your information if required by law, or to protect the rights, property, or safety of Ouro, our players, or others. We will inform you where legally permitted to do so.",
                  },
                ]}
              />
              <Callout>
                <Typography component="p">
                  <strong>We do not display advertising.</strong> Ouro doesn&apos;t run ads, doesn&apos;t work
                  with advertisers, and doesn&apos;t share data with ad networks or data brokers in any form.
                </Typography>
              </Callout>
            </DocSection>

            <DocSection id="s4" num="04" title="Cookies and tracking">
              <Typography sx={bodySx}>
                Ouro uses cookies and similar technologies to keep the platform functional and understand how
                it&apos;s being used. We try to keep this minimal.
              </Typography>
              <Typography sx={bodySx}>
                Cookies are small data files stored on your device that help us remember your preferences,
                maintain your session, and understand how you interact with the service. They are not used to
                build advertising profiles or track you across the web.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                You can manage or disable cookies through your browser settings. Disabling certain cookies may
                affect how the platform functions — session cookies in particular are required for login to
                work.
              </Typography>
            </DocSection>

            <DocSection id="s5" num="05" title="Data security">
              <Typography sx={bodySx}>
                We take the security of your data seriously and implement a range of technical and
                organisational measures to protect it from unauthorised access, alteration, disclosure, or
                destruction. These include encrypted data transmission, access controls, and regular security
                reviews.
              </Typography>
              <Callout>
                <Typography component="p">
                  <strong>Honest caveat:</strong> No method of transmission over the internet is completely
                  secure. We cannot guarantee absolute security — but we commit to being transparent about any
                  incidents that affect your data, and to responding quickly when they occur.
                </Typography>
              </Callout>
            </DocSection>

            <DocSection id="s6" num="06" title="Your rights">
              <Typography sx={bodySx}>
                You have the following rights regarding your personal data held by Ouro. These rights apply
                regardless of your membership tier.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: "2px",
                  my: 2.5,
                }}
              >
                {RIGHTS.map((right) => (
                  <Box
                    key={right.name}
                    sx={{
                      background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                      border: `1px solid ${colors.border.subtle}`,
                      p: "20px 22px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "13px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: colors.text.primary,
                        mb: 0.75,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&::before": {
                          content: '""',
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          bgcolor: colors.primary.main,
                          flexShrink: 0,
                        },
                      }}
                    >
                      {right.name}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: colors.text.secondary, lineHeight: 1.6 }}>
                      {right.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={bodySx}>
                To exercise any of these rights, contact us at{" "}
                <Box component="a" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </Box>
                . If you&apos;re signed in, you can also permanently delete your account from{" "}
                <strong>Account Settings → Danger Zone</strong>.
              </Typography>
              <Callout>
                <Typography component="p">
                  For GDPR-specific rights — including data portability and the right to object to processing
                  — see the{" "}
                  <Box component={Link} to="/data-protection-policy-and-gdpr">
                    GDPR Policy
                  </Box>
                  .
                </Typography>
              </Callout>
            </DocSection>

            <DocSection id="s7" num="07" title="Children's privacy">
              <Typography sx={bodySx}>
                Ouro is not intended for children under the age of 13 and we do not knowingly collect personal
                information from anyone under this age.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                If we become aware that we have inadvertently collected personal information from a child under
                13, we will take steps to delete it immediately. If you believe this has occurred, contact us
                at{" "}
                <Box component="a" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </Box>
                .
              </Typography>
            </DocSection>

            <DocSection id="s8" num="08" title="Policy changes">
              <Typography sx={bodySx}>
                We may update this Privacy Policy from time to time to reflect changes in our practices,
                technology, or legal requirements.
              </Typography>
              <Typography sx={bodySx}>
                We will notify you of any significant changes by posting the updated policy on this page and
                updating the &quot;Last Updated&quot; date. For material changes that affect your rights or how
                we use your data, we&apos;ll send a notification to the email address associated with your
                account.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                Continued use of Ouro after a policy change takes effect constitutes your acceptance of the
                revised policy. If you disagree with a change, you can close your account and request data
                deletion at any time.
              </Typography>
            </DocSection>

            <DocSection id="s9" num="09" title="Contact us">
              <Typography sx={bodySx}>
                If you have any questions or concerns about this Privacy Policy or our data practices, reach
                out directly. We don&apos;t route privacy inquiries through a chatbot.
              </Typography>
              <Box
                sx={{
                  mt: 2.5,
                  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                  border: `1px solid ${colors.border.subtle}`,
                  p: "24px 28px",
                }}
              >
                {[
                  {
                    label: "Email",
                    icon: <MailOutlineRounded sx={{ fontSize: 14 }} />,
                    val: (
                      <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
                        {SUPPORT_EMAIL}
                      </Box>
                    ),
                  },
                  {
                    label: "Support ticket",
                    icon: <ConfirmationNumberOutlined sx={{ fontSize: 14 }} />,
                    val: (
                      <Box component={Link} to="/support" sx={{ color: colors.primary.main, textDecoration: "none" }}>
                        ouro.is/support
                      </Box>
                    ),
                  },
                  {
                    label: "Community",
                    icon: <DiscordIcon />,
                    val: (
                      <Box
                        component="a"
                        href={DISCORD_INVITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: colors.primary.main, textDecoration: "none" }}
                      >
                        Discord server
                      </Box>
                    ),
                  },
                ].map((row, index, arr) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.75,
                      borderBottom: index < arr.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                    }}
                  >
                    <Box sx={contactIconSx}>{row.icon}</Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', system-ui, sans-serif",
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: colors.text.muted,
                          mb: 0.25,
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: colors.text.secondary }}>{row.val}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </DocSection>
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
